import { cn } from "@krea/shared/utils";

const EMPTY = "#DCDCE6";

const SCORE_COLORS: [threshold: number, color: string][] = [
  [1.5, "#FF3722"],
  [2.5, "#FF8622"],
  [3.5, "#FFCE00"],
  [4, "#73CF11"],
  [5, "#00B67A"],
];

function getScoreColor(score: number): string {
  if (score <= 0) return EMPTY;
  return SCORE_COLORS.find(([t]) => score <= t)?.[1] ?? "#00B67A";
}

function normalizeScore(score: number): number {
  const clamped = Math.max(0, Math.min(5, score));
  return Math.round(clamped * 2) / 2;
}

const STAR_PATH =
  "M12,16.175 L15.65,15.25 L17.175,19.95 L12,16.175 Z M20.4,10.1 L13.975,10.1 L12,4.05 L10.025,10.1 L3.6,10.1 L8.8,13.85 L6.825,19.9 L12.025,16.15 L15.225,13.85 L20.4,10.1 Z";

interface ITrustpilotStarsProps {
  score?: number;
  className?: string;
}

export const TrustpilotStars = ({
  score = 0,
  className,
}: ITrustpilotStarsProps) => {
  const normalized = normalizeScore(score);
  const filledCount = Math.floor(normalized);
  const hasHalf = normalized % 1 !== 0;
  const fillColor = getScoreColor(normalized);

  return (
    <div className={cn("inline-flex h-full w-auto", className)}>
      <svg
        viewBox="0 0 128 24"
        xmlns="http://www.w3.org/2000/svg"
        className="h-full w-auto"
        // Decorative: the exact score is rendered as text by the consumer. A
        // label here would announce the half-star-ROUNDED value (4.7 → 4.5).
        aria-hidden="true"
        focusable="false"
      >
        {[0, 1, 2, 3, 4].map((i) => {
          const x = i * 26;
          const isFilled = i < filledCount;
          const isHalf = i === filledCount && hasHalf;

          return (
            <g key={i}>
              {isHalf ? (
                <>
                  <rect x={x + 12} y={0} width={12} height={24} fill={EMPTY} />
                  <rect x={x} y={0} width={12} height={24} fill={fillColor} />
                </>
              ) : (
                <rect
                  x={x}
                  y={0}
                  width={24}
                  height={24}
                  fill={isFilled ? fillColor : EMPTY}
                />
              )}
              <path
                d={STAR_PATH}
                fill="#FFFFFF"
                transform={`translate(${x}, 0)`}
              />
            </g>
          );
        })}
      </svg>
    </div>
  );
};
